import React, { useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  controlHeights,
  inputFrameClasses,
  inputRoundedClasses,
} from './KoobInputText';

const MAX_DIGITS = 5;

// One joined control (button group): [−][value][+] inside a single input
// frame, so it lines up with KoobInputText/KoobInputSelect in form rows.
const stepperGroup = cva(
  // self-start keeps the group at its natural width inside stretching
  // flex/grid parents (align-items defaults to stretch there).
  `inline-flex items-stretch self-start shrink-0 overflow-hidden ${inputRoundedClasses.md} ${inputFrameClasses} transition-colors focus-within:border-k-h focus-within:ring-4 focus-within:ring-navy-100`,
  {
    variants: {
      size: {
        sm: controlHeights.sm,
        md: controlHeights.md,
      },
      error: {
        true: 'border-red-500 ring-2 ring-red-500/20',
      },
      disabled: {
        true: 'cursor-not-allowed !bg-navy-50 text-navy-400',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

const stepperButton = cva(
  'inline-flex shrink-0 items-center justify-center border-none bg-transparent font-medium leading-none text-gray-500 cursor-pointer transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
  {
    variants: {
      size: {
        sm: 'w-8 text-base',
        md: 'w-10 text-lg',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

const stepperInput = cva(
  // selection tint: keyboard focus select-alls the value and the browser's
  // blue highlight box reads as a blue focus outline around the number.
  // focus:border-input — the TO app runs @tailwindcss/forms globally, whose
  // input:focus rule painted the x-borders blue-600 (DMC uses strategy
  // 'class' and never hit it).
  'border-0 border-x border-solid bg-transparent p-0 text-center font-medium focus:outline-none focus:ring-0 selection:bg-brand/15 disabled:cursor-not-allowed',
  {
    variants: {
      size: {
        sm: 'w-10 text-sm',
        md: 'w-12 text-base md:text-sm',
      },
      error: {
        true: 'border-red-200 text-red-600 focus:border-red-200',
        false: 'border-input text-k-h focus:border-input',
      },
    },
    defaultVariants: { size: 'md', error: false },
  },
);

type KoobInputStepperProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'className' | 'size' | 'value' | 'onChange' | 'min' | 'max' | 'type'
> &
  VariantProps<typeof stepperButton> & {
    value?: number | null;
    onValueChange: (value: number) => void;
    min?: number;
    max?: number;
    /** Force-disable the − button (e.g. a permission gate), independent of min. */
    allowDecrement?: boolean;
    error?: boolean;
  };

/**
 * Numeric −/+ stepper. The center value is directly editable; typed values
 * are clamped to [min, max]. Replaces the legacy margaret `Stepper` and the
 * per-app `StepperField` wrappers (`onChange` → `onValueChange`,
 * `source` → `data-testid`); for formik forms use `KoobInputStepperField`.
 */
const KoobInputStepper = React.forwardRef<
  HTMLInputElement,
  KoobInputStepperProps
>(
  (
    {
      value,
      onValueChange,
      min = 0,
      max = Number.MAX_SAFE_INTEGER,
      allowDecrement = true,
      disabled,
      size,
      error,
      onBlur,
      ...props
    },
    ref,
  ) => {
    // Lets the field go temporarily empty while typing; snaps back on blur.
    const [draft, setDraft] = useState<string | null>(null);

    const clamp = (next: number) => Math.min(max, Math.max(min, next));

    // Typing only caps at max: raising a value to min mid-entry would turn
    // "15" into "25" when min is 2. The lower bound is applied on blur.
    const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value.replace(/\D/g, '').slice(0, MAX_DIGITS);
      if (raw === '') {
        setDraft('');
        return;
      }
      const typed = Math.min(max, parseInt(raw, 10));
      setDraft(String(typed));
      if (typed >= min) {
        onValueChange(typed);
      }
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      if (draft !== null && draft !== '' && parseInt(draft, 10) < min) {
        onValueChange(min);
      }
      setDraft(null);
      onBlur?.(event);
    };

    // A click does not blur the field in every browser (Firefox and Safari on
    // macOS leave the focus where it was), so a pending draft is dropped here
    // or it would keep hiding the stepped value.
    const handleStep = (delta: number) => {
      setDraft(null);
      onValueChange(clamp((value ?? 0) + delta));
    };

    return (
      <div className={stepperGroup({ size, error, disabled })}>
        <button
          type="button"
          aria-label="Decrement"
          className={stepperButton({ size })}
          disabled={disabled || !allowDecrement || (value ?? 0) <= min}
          onClick={() => handleStep(-1)}
        >
          −
        </button>
        <input
          ref={ref}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          className={stepperInput({ size, error })}
          value={draft ?? (value == null ? '' : String(value))}
          onChange={handleInput}
          onFocus={(event) => event.currentTarget.select()}
          onBlur={handleBlur}
          disabled={disabled}
          {...props}
        />
        <button
          type="button"
          aria-label="Increment"
          className={stepperButton({ size })}
          disabled={disabled || (value ?? 0) >= max}
          onClick={() => handleStep(1)}
        >
          +
        </button>
      </div>
    );
  },
);

KoobInputStepper.displayName = 'KoobInputStepper';

export default KoobInputStepper;
